import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { invalidateOnboardingCache } from "@/App";
import { useAuth } from "@/context/AuthContext";
import { User, Camera, Image as ImageIcon, Check, X, Sparkles, Trash2 } from "lucide-react";
import TopBar from "@/components/TopBar";
import api, { formatApiError } from "@/lib/api";
import { useToast } from "@/components/Toast";

const BODY_TYPES = ["Slim", "Athletic", "Average", "Muscular", "Curvy", "Plus"];
const AGE_RANGES = ["18-24", "25-34", "35-44", "45-54", "55+"];
const GENDERS = ["Male", "Female", "Non-binary", "Prefer not to say"];
const STYLE_OPTIONS = [
  "streetwear","casual","old money","luxury","business casual","smart casual",
  "athletic","minimalist","vintage","y2k","techwear","formal",
];
const GOAL_OPTIONS = [
  { value: "fashion",      label: "Better fashion" },
  { value: "glow-up",      label: "Overall glow-up" },
  { value: "confidence",   label: "Build confidence" },
  { value: "dating",       label: "Look great for dating" },
  { value: "weight-loss",  label: "Lose fat" },
  { value: "weight-gain",  label: "Build muscle / gain weight" },
  { value: "fitness",      label: "Get fitter" },
  { value: "nutrition",    label: "Eat better" },
  { value: "skincare",     label: "Clear / better skin" },
  { value: "hair",         label: "Better hair" },
];
const FITNESS_GOAL_OPTIONS = [
  { value: "lose-fat",      label: "Lose fat" },
  { value: "build-muscle",  label: "Build muscle" },
  { value: "get-fitter",    label: "General fitness" },
  { value: "mobility",      label: "Mobility / posture" },
  { value: "endurance",     label: "Endurance / cardio" },
];
const DIETARY_GOAL_OPTIONS = [
  { value: "high-protein",  label: "High protein" },
  { value: "low-carb",      label: "Low carb" },
  { value: "balanced",      label: "Balanced" },
  { value: "plant-based",   label: "Plant-based" },
  { value: "dairy-free",    label: "Dairy-free" },
  { value: "gluten-free",   label: "Gluten-free" },
  { value: "low-sugar",     label: "Low sugar" },
];
const NUTRITION_GOAL_OPTIONS = [
  { value: "lose-weight",    label: "Lose weight" },
  { value: "gain-muscle",    label: "Gain muscle" },
  { value: "maintain",       label: "Maintain weight" },
  { value: "recomp",         label: "Body recomposition" },
  { value: "eat-healthier",  label: "Eat healthier" },
];
const BUDGET_BAND_OPTIONS = [
  { value: "under-50",  label: "Under $50" },
  { value: "50-plus",   label: "$50+" },
  { value: "100-plus",  label: "$100+" },
  { value: "250-plus",  label: "$250+" },
  { value: "500-plus",  label: "$500+" },
  { value: "luxury",    label: "Luxury · No budget" },
];
const HAIR_GOAL_OPTIONS = [
  { value: "grow-faster",     label: "Grow hair faster" },
  { value: "hair-loss",       label: "Hair loss" },
  { value: "dry-hair",        label: "Dry hair" },
  { value: "oily-hair",       label: "Oily hair" },
  { value: "dandruff",        label: "Dandruff" },
  { value: "repair-damage",   label: "Repair damage" },
  { value: "better-smell",    label: "Better smell" },
];
const BEARD_GOAL_OPTIONS = [
  { value: "grow-fuller",   label: "Grow fuller" },
  { value: "fix-patchy",    label: "Fix patchy areas" },
  { value: "softer",        label: "Softer beard" },
  { value: "better-smell",  label: "Better smell" },
  { value: "beard-routine", label: "Daily routine" },
  { value: "trimmer-recs",  label: "Trimmer recs" },
];
const SKIN_CONDITION_OPTIONS = [
  { value: "eczema", label: "Eczema" },
  { value: "rosacea", label: "Rosacea" },
  { value: "psoriasis", label: "Psoriasis" },
  { value: "acne-rx", label: "Severe acne (under treatment)" },
  { value: "melasma", label: "Melasma" },
];
const SCALP_CONDITION_OPTIONS = [
  { value: "dandruff", label: "Dandruff" },
  { value: "seborrheic-dermatitis", label: "Seborrheic dermatitis" },
  { value: "psoriasis", label: "Scalp psoriasis" },
  { value: "alopecia", label: "Alopecia" },
];
const ALLERGY_OPTIONS = [
  { value: "fragrance", label: "Fragrance" },
  { value: "sulfates", label: "Sulfates (SLS/SLES)" },
  { value: "parabens", label: "Parabens" },
  { value: "nuts", label: "Tree nuts" },
  { value: "dairy", label: "Dairy" },
  { value: "gluten", label: "Gluten" },
  { value: "shellfish", label: "Shellfish" },
];
const RX_TREATMENT_OPTIONS = [
  { value: "tretinoin",       label: "Tretinoin / retinoid" },
  { value: "hydroquinone",    label: "Hydroquinone" },
  { value: "topical-steroid", label: "Topical steroid" },
  { value: "minoxidil",       label: "Minoxidil / finasteride" },
  { value: "accutane",        label: "Accutane" },
  { value: "other",           label: "Other (in notes)" },
];

// NEW (2026-03 onboarding update) — extra Profile fields
const MAIN_GOAL_OPTIONS = [
  { value: "gain-muscle",     label: "Gain Muscle" },
  { value: "lose-weight",     label: "Lose Weight" },
  { value: "maintain-weight", label: "Maintain Weight" },
  { value: "improve-health",  label: "Improve Overall Health" },
];
const ACTIVITY_LEVEL_OPTIONS = [
  { value: "sedentary",         label: "Sedentary" },
  { value: "lightly-active",    label: "Lightly Active" },
  { value: "moderately-active", label: "Moderately Active" },
  { value: "very-active",       label: "Very Active" },
  { value: "athlete",           label: "Athlete" },
];
const WORKOUT_EXP_OPTIONS = [
  { value: "beginner",     label: "Beginner" },
  { value: "intermediate", label: "Intermediate" },
  { value: "advanced",     label: "Advanced" },
];
const WORKOUT_LOCATION_OPTIONS = [
  { value: "gym",  label: "Gym" },
  { value: "home", label: "Home" },
  { value: "both", label: "Both" },
];
const DIETARY_PREF_OPTIONS = [
  { value: "no-pref",       label: "No Preference" },
  { value: "high-protein",  label: "High Protein" },
  { value: "vegetarian",    label: "Vegetarian" },
  { value: "vegan",         label: "Vegan" },
  { value: "keto",          label: "Keto" },
  { value: "mediterranean", label: "Mediterranean" },
  { value: "low-carb",      label: "Low Carb" },
];
const UNITS_OPTIONS = [
  { value: "imperial", label: "Imperial (lb, in, oz)" },
  { value: "metric",   label: "Metric (kg, cm, ml)" },
];



async function fileToResizedBase64(file, maxDim = 1280) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        let { width, height } = img;
        const s = Math.min(1, maxDim / Math.max(width, height));
        width = Math.round(width * s);
        height = Math.round(height * s);
        const c = document.createElement("canvas");
        c.width = width; c.height = height;
        c.getContext("2d").drawImage(img, 0, 0, width, height);
        resolve(c.toDataURL("image/jpeg", 0.85));
      };
      img.onerror = () => reject(new Error("Could not decode image"));
      img.src = reader.result;
    };
    reader.onerror = () => reject(new Error("Could not read file"));
    reader.readAsDataURL(file);
  });
}

function Field({ label, hint, children, testId }) {
  return (
    <div className="field" data-testid={testId}>
      <label className="label">{label}</label>
      {children}
      {hint && <div className="tiny" style={{ color: "rgba(255,255,255,0.4)", marginTop: 6 }}>{hint}</div>}
    </div>
  );
}

function ChipRow({ options, value, onPick, getId, dt }) {
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
      {options.map((o) => {
        const v = typeof o === "string" ? o : o.value;
        const label = typeof o === "string" ? o : o.label;
        return (
          <button
            key={v}
            type="button"
            className={`chip ${value === v ? "active" : ""}`}
            onClick={() => onPick(v)}
            data-testid={getId ? getId(v) : `${dt}-${(label || "").toString().toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
          >
            {label}
          </button>
        );
      })}
    </div>
  );
}

function MultiChipRow({ options, value, onToggle, dt }) {
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
      {options.map((o) => {
        const v = typeof o === "string" ? o : o.value;
        const label = typeof o === "string" ? o : o.label;
        const active = value.includes(v);
        return (
          <button
            key={v}
            type="button"
            className={`chip ${active ? "active" : ""}`}
            onClick={() => onToggle(v)}
            data-testid={`${dt}-${v.replace(/[^a-z0-9]+/gi, "-")}`}
          >
            {label}
          </button>
        );
      })}
    </div>
  );
}

export default function ProfileSetupScreen() {
  const navigate = useNavigate();
  // Onboarding cache lives at App-level for perf. If we don't invalidate it
  // here after a successful PUT /profile, the /home guard reads the stale
  // "not onboarded" value and bounces the user back to /onboarding — which
  // is exactly the "returns to login" bug Apple's reviewer hit.
  const { refresh } = useAuth();
  const toast = useToast();
  const galleryRef = useRef(null);
  const cameraRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [analyzing, setAnalyzing] = useState(false);

  const [profile, setProfile] = useState({
    height: "", weight: "", body_type: "",
    age_range: "", gender: "", style_prefs: [], goals: [],
    fitness_goals: [], dietary_goals: [],
    nutrition_goal: "",
    budget_band: "",
    hair_goals: [], beard_goals: [],
    has_medical_considerations: null,
    skin_conditions: [], scalp_conditions: [], allergies: [],
    prescription_treatments: [], pregnancy_breastfeeding: null,
    other_medical_notes: "",
    // 2026-03 onboarding update — new optional fields
    main_goal: "",
    activity_level: "",
    goal_weight: "",
    workout_experience: "",
    workout_location: "",
    dietary_preference: "",
    food_allergies: [],
    medical_dietary_restrictions: [],
    preferred_units: "",
    daily_water_goal_ml: "",
    sensitive_skin: null,
    prescription_skincare: [],
    ingredient_allergies: [],
    appearance: null, photo_base64: null, photo_mime: "image/jpeg",
  });

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/profile");
        if (!alive) return;
        setProfile({
          height: data.height || "",
          weight: data.weight || "",
          body_type: data.body_type || "",
          age_range: data.age_range || "",
          gender: data.gender || "",
          style_prefs: data.style_prefs || [],
          goals: data.goals || [],
          fitness_goals: data.fitness_goals || [],
          dietary_goals: data.dietary_goals || [],
          nutrition_goal: data.nutrition_goal || "",
          budget_band: data.budget_band || "",
          hair_goals: data.hair_goals || [],
          beard_goals: data.beard_goals || [],
          has_medical_considerations: data.has_medical_considerations ?? null,
          skin_conditions: data.skin_conditions || [],
          scalp_conditions: data.scalp_conditions || [],
          allergies: data.allergies || [],
          prescription_treatments: data.prescription_treatments || [],
          pregnancy_breastfeeding: data.pregnancy_breastfeeding ?? null,
          other_medical_notes: data.other_medical_notes || "",
          main_goal: data.main_goal || "",
          activity_level: data.activity_level || "",
          goal_weight: data.goal_weight || "",
          workout_experience: data.workout_experience || "",
          workout_location: data.workout_location || "",
          dietary_preference: data.dietary_preference || "",
          food_allergies: data.food_allergies || [],
          medical_dietary_restrictions: data.medical_dietary_restrictions || [],
          preferred_units: data.preferred_units || "",
          daily_water_goal_ml: data.daily_water_goal_ml ? String(data.daily_water_goal_ml) : "",
          sensitive_skin: data.sensitive_skin ?? null,
          prescription_skincare: data.prescription_skincare || [],
          ingredient_allergies: data.ingredient_allergies || [],
          appearance: data.appearance || null,
          photo_base64: data.photo_base64 || null,
          photo_mime: data.photo_mime || "image/jpeg",
        });
      } catch (err) {
        setError(formatApiError(err));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const update = (patch) => setProfile((p) => ({ ...p, ...patch }));
  const updateSize = (k, v) => setProfile((p) => ({ ...p, clothing_sizes: { ...p.clothing_sizes, [k]: v } }));
  const toggleStyle = (s) =>
    update({ style_prefs: profile.style_prefs.includes(s)
      ? profile.style_prefs.filter((x) => x !== s)
      : [...profile.style_prefs, s] });
  const toggleGoal = (g) =>
    update({ goals: profile.goals.includes(g)
      ? profile.goals.filter((x) => x !== g)
      : [...profile.goals, g] });
  const toggleFitness = (g) =>
    update({ fitness_goals: profile.fitness_goals.includes(g)
      ? profile.fitness_goals.filter((x) => x !== g)
      : [...profile.fitness_goals, g] });
  const toggleDiet = (g) =>
    update({ dietary_goals: profile.dietary_goals.includes(g)
      ? profile.dietary_goals.filter((x) => x !== g)
      : [...profile.dietary_goals, g] });
  const toggleHair = (g) =>
    update({ hair_goals: profile.hair_goals.includes(g)
      ? profile.hair_goals.filter((x) => x !== g)
      : [...profile.hair_goals, g] });
  const toggleBeard = (g) =>
    update({ beard_goals: profile.beard_goals.includes(g)
      ? profile.beard_goals.filter((x) => x !== g)
      : [...profile.beard_goals, g] });
  const toggleMedList = (key, v) =>
    update({ [key]: profile[key].includes(v)
      ? profile[key].filter((x) => x !== v)
      : [...profile[key], v] });

  const onPhotoPicked = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!/image\/(jpeg|jpg|png|webp)/i.test(file.type)) {
      toast.show("Please pick a JPEG, PNG, or WEBP image.", "error"); return;
    }
    try {
      const dataUrl = await fileToResizedBase64(file, 1280);
      update({ photo_base64: dataUrl, photo_mime: "image/jpeg" });
      setAnalyzing(true);
      try {
        const b64 = dataUrl.split(",", 2)[1];
        const { data } = await api.post("/appearance/analyze", { image_base64: b64, image_mime: "image/jpeg" });
        const appearance = data.appearance;
        const detected = appearance?.detected || {};
        update({
          appearance,
          body_type: profile.body_type || (
            /athletic/i.test(detected.body_proportions || "") ? "Athletic" :
            /slim|slender/i.test(detected.body_proportions || "") ? "Slim" :
            /muscular/i.test(detected.body_proportions || "") ? "Muscular" :
            /curvy/i.test(detected.body_proportions || "") ? "Curvy" :
            /plus|larger/i.test(detected.body_proportions || "") ? "Plus" :
            "Average"
          ),
        });
        toast.show("Photo analyzed — confirm your details below.", "success");
      } catch (err) {
        toast.show(formatApiError(err) || "Photo analyze failed — fill the form manually.", "error");
      } finally {
        setAnalyzing(false);
      }
    } catch {
      toast.show("Could not read that image.", "error");
    }
  };

  const removePhoto = () => update({ photo_base64: null, appearance: null });

  const save = async () => {
    setSaving(true); setError("");
    try {
      // Normalise the few numeric / list fields before sending.
      const body = {
        ...profile,
        daily_water_goal_ml: profile.daily_water_goal_ml
          ? parseInt(profile.daily_water_goal_ml, 10) || null
          : null,
      };
      await api.put("/profile", body);
      // ORDER MATTERS: server has now marked the user as onboarded, but the
      // App-level cache in RequireOnboarding is stale. Invalidate FIRST, then
      // refresh the auth context so any consumer-side flags (e.g. email
      // verified banner) are also current, THEN navigate. Without this the
      // /home guard reads its stale cache, believes the user is still
      // un-onboarded, and bounces them to /onboarding — which forwards to
      // the Welcome screen ("returns to login" — Apple rejection 2.1a).
      invalidateOnboardingCache();
      try { await refresh(); } catch { /* non-fatal */ }
      toast.show("Profile saved", "success");
      // Use replace so tapping back on the home screen doesn't dump the user
      // back into the profile-setup form.
      navigate("/home", { replace: true });
    } catch (err) {
      setError(formatApiError(err));
    } finally { setSaving(false); }
  };

  const wipe = async () => {
    if (!window.confirm("Clear your saved profile?")) return;
    try {
      await api.delete("/profile");
      setProfile({
        height: "", weight: "", body_type: "",
        age_range: "", gender: "", style_prefs: [], goals: [],
        fitness_goals: [], dietary_goals: [],
        nutrition_goal: "",
        budget_band: "",
        hair_goals: [], beard_goals: [],
        has_medical_considerations: null,
        skin_conditions: [], scalp_conditions: [], allergies: [],
        prescription_treatments: [], pregnancy_breastfeeding: null,
        other_medical_notes: "",
        main_goal: "",
        activity_level: "",
        goal_weight: "",
        workout_experience: "",
        workout_location: "",
        dietary_preference: "",
        food_allergies: [],
        medical_dietary_restrictions: [],
        preferred_units: "",
        daily_water_goal_ml: "",
        sensitive_skin: null,
        prescription_skincare: [],
        ingredient_allergies: [],
        appearance: null, photo_base64: null, photo_mime: "image/jpeg",
      });
      toast.show("Profile cleared", "success");
    } catch (err) {
      toast.show(formatApiError(err), "error");
    }
  };

  if (loading) {
    return (
      <div className="screen" data-testid="profile-loading">
        <TopBar title="BUILD MY PROFILE" />
        <div style={{ display: "flex", justifyContent: "center", padding: 40 }}><div className="spinner" /></div>
      </div>
    );
  }

  return (
    <div className="screen fade-in" data-testid="profile-screen">
      <TopBar title="BUILD MY PROFILE" />
      <div style={{ marginBottom: 18 }}>
        <div className="eyebrow" style={{ marginBottom: 10 }}>YOUR STYLE DNA</div>
        <h2 className="h1" style={{ fontSize: 34 }}>
          Describe <em className="serif" style={{ fontStyle: "italic", fontWeight: 300 }}>yourself</em>
        </h2>
        <p className="body" style={{ marginTop: 12 }}>
          Saved once. Used on every outfit. The more we know, the better the recommendations.
        </p>
      </div>

      {/* Personalisation tip — explains the live-update behaviour */}
      <div
        className="card"
        data-testid="profile-personalise-tip"
        style={{
          padding: 14,
          marginBottom: 16,
          border: "1px solid rgba(232,211,185,0.30)",
          background: "rgba(232,211,185,0.05)",
          display: "flex", gap: 12, alignItems: "flex-start",
        }}
      >
        <Sparkles size={16} color="var(--gold)" strokeWidth={1.8} style={{ flexShrink: 0, marginTop: 2 }} />
        <div className="body-sm" style={{ color: "var(--text)", lineHeight: 1.55 }}>
          Everything here is <strong>optional</strong>. The more you add — fitness goals,
          style, dietary, medical — the more your AI coaches can personalise.
          Updates apply instantly across every coach.
        </div>
      </div>

      {/* Photo path */}
      <div className="outfit-section" data-testid="profile-photo-section">
        <div className="eyebrow" style={{ marginBottom: 12, display: "flex", alignItems: "center", gap: 8 }}>
          <Sparkles size={12} strokeWidth={1.8} /> FAST PATH — UPLOAD A FULL-BODY PHOTO
        </div>
        <p className="body-sm" style={{ marginBottom: 14, color: "rgba(255,255,255,0.6)" }}>
          Optional. AI detects your build and proportions automatically. Used to render outfit previews on someone with your build.
        </p>
        {profile.photo_base64 ? (
          <div style={{ position: "relative", display: "inline-block" }}>
            <img
              src={profile.photo_base64.startsWith("data:") ? profile.photo_base64 : `data:${profile.photo_mime || "image/jpeg"};base64,${profile.photo_base64}`}
              alt="you"
              className="preview-img"
              style={{ maxHeight: 240, borderRadius: 14 }}
              data-testid="profile-photo-preview"
            />
            <button
              type="button"
              className="icon-btn"
              style={{ position: "absolute", top: 8, right: 8 }}
              onClick={removePhoto}
              aria-label="Remove photo"
              data-testid="profile-photo-remove"
            >
              <X size={14} />
            </button>
            {analyzing && (
              <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(0,0,0,0.55)", borderRadius: 14 }}>
                <div className="spinner" />
                <div className="tiny" style={{ marginLeft: 10, letterSpacing: "0.18em", textTransform: "uppercase" }}>Analyzing…</div>
              </div>
            )}
          </div>
        ) : (
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <button className="btn btn-outline" onClick={() => cameraRef.current?.click()} data-testid="profile-camera-btn">
              <Camera size={14} /> Take photo
            </button>
            <button className="btn btn-outline" onClick={() => galleryRef.current?.click()} data-testid="profile-gallery-btn">
              <ImageIcon size={14} /> Choose from gallery
            </button>
            <input ref={cameraRef}  type="file" accept="image/jpeg,image/png,image/webp" capture="environment" hidden onChange={onPhotoPicked} data-testid="profile-camera-input" />
            <input ref={galleryRef} type="file" accept="image/jpeg,image/png,image/webp" hidden onChange={onPhotoPicked} data-testid="profile-gallery-input" />
          </div>
        )}
      </div>

      {/* Manual form */}
      <div className="outfit-section" data-testid="profile-form-section">
        <div className="eyebrow" style={{ marginBottom: 14 }}>YOUR DETAILS</div>

        <Field label="Height" testId="profile-field-height">
          <input
            className="input" placeholder={`e.g. 6'0" or 183 cm`}
            value={profile.height} onChange={(e) => update({ height: e.target.value })}
            data-testid="profile-height-input"
          />
        </Field>

        <Field label="Weight" testId="profile-field-weight">
          <input
            className="input" placeholder="e.g. 185 lbs or 84 kg"
            value={profile.weight} onChange={(e) => update({ weight: e.target.value })}
            data-testid="profile-weight-input"
          />
        </Field>

        <Field label="Main goal" testId="profile-field-main-goal" hint="Drives your calorie target. You can switch anytime.">
          <ChipRow options={MAIN_GOAL_OPTIONS} value={profile.main_goal} onPick={(v) => update({ main_goal: profile.main_goal === v ? "" : v })} dt="profile-main-goal" />
        </Field>

        <Field label="Activity level" testId="profile-field-activity-level" hint="Be honest — this multiplies your BMR.">
          <ChipRow options={ACTIVITY_LEVEL_OPTIONS} value={profile.activity_level} onPick={(v) => update({ activity_level: profile.activity_level === v ? "" : v })} dt="profile-activity-level" />
        </Field>

        <Field label="Body type" testId="profile-field-body">
          <ChipRow options={BODY_TYPES} value={profile.body_type} onPick={(v) => update({ body_type: v })} dt="profile-body" />
        </Field>

        <Field label="Age range" testId="profile-field-age">
          <ChipRow options={AGE_RANGES} value={profile.age_range} onPick={(v) => update({ age_range: v })} dt="profile-age" />
        </Field>

        <Field label="Gender (optional)" testId="profile-field-gender">
          <ChipRow options={GENDERS} value={profile.gender} onPick={(v) => update({ gender: v })} dt="profile-gender" />
        </Field>

        <Field label="Style preferences (pick any)" testId="profile-field-styles">
          <MultiChipRow options={STYLE_OPTIONS} value={profile.style_prefs} onToggle={toggleStyle} dt="profile-style" />
        </Field>

        <Field label="Your glow-up goals (pick any)" testId="profile-field-goals" hint="High-level goals — drives stylist, coach, and daily brief priorities.">
          <MultiChipRow options={GOAL_OPTIONS} value={profile.goals} onToggle={toggleGoal} dt="profile-goal" />
        </Field>

        <Field label="Fitness goals" testId="profile-field-fitness-goals" hint="Used by Coach + Nutrition to set targets that match how you train.">
          <MultiChipRow options={FITNESS_GOAL_OPTIONS} value={profile.fitness_goals} onToggle={toggleFitness} dt="profile-fitness" />
        </Field>

        <Field label="Dietary goals" testId="profile-field-dietary-goals" hint="Used by the Nutrition Scanner verdicts and meal swap suggestions.">
          <MultiChipRow options={DIETARY_GOAL_OPTIONS} value={profile.dietary_goals} onToggle={toggleDiet} dt="profile-diet" />
        </Field>

        <Field label="Nutrition goal" testId="profile-field-nutrition-goal" hint="Every food scan judges the meal against this exact goal.">
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {NUTRITION_GOAL_OPTIONS.map((o) => (
              <button
                key={o.value}
                type="button"
                className={`chip ${profile.nutrition_goal === o.value ? "active" : ""}`}
                onClick={() => update({ nutrition_goal: profile.nutrition_goal === o.value ? "" : o.value })}
                data-testid={`profile-nutrition-goal-${o.value}`}
              >
                {o.label}
              </button>
            ))}
          </div>
        </Field>

        <Field label="Spending budget per item" testId="profile-field-budget-band" hint="Shared across Style, Cologne, Grooming. We'll match products to your range.">
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {BUDGET_BAND_OPTIONS.map((o) => (
              <button
                key={o.value}
                type="button"
                className={`chip ${profile.budget_band === o.value ? "active" : ""}`}
                onClick={() => update({ budget_band: profile.budget_band === o.value ? "" : o.value })}
                data-testid={`profile-budget-band-${o.value}`}
              >
                {o.label}
              </button>
            ))}
          </div>
        </Field>

        <Field label="Hair goals" testId="profile-field-hair-goals" hint="Used by the Hair Coach to personalise routines.">
          <MultiChipRow options={HAIR_GOAL_OPTIONS} value={profile.hair_goals} onToggle={toggleHair} dt="profile-hair" />
        </Field>

        <Field label="Beard goals" testId="profile-field-beard-goals" hint="Used by the Beard Coach to personalise routines.">
          <MultiChipRow options={BEARD_GOAL_OPTIONS} value={profile.beard_goals} onToggle={toggleBeard} dt="profile-beard" />
        </Field>

        {/* Medical Safety Profile — optional, gated by a Yes/No */}
        <Field
          label="Health & safety (optional)"
          testId="profile-field-medical"
          hint="Helps us avoid allergens and remind you to check with a professional when needed."
        >
          <div style={{ display: "flex", gap: 6, marginBottom: 12 }}>
            <button
              type="button"
              className={`chip ${profile.has_medical_considerations === false ? "active" : ""}`}
              onClick={() => update({ has_medical_considerations: profile.has_medical_considerations === false ? null : false })}
              data-testid="profile-medical-none"
            >
              I have no medical considerations
            </button>
            <button
              type="button"
              className={`chip ${profile.has_medical_considerations === true ? "active" : ""}`}
              onClick={() => update({ has_medical_considerations: profile.has_medical_considerations === true ? null : true })}
              data-testid="profile-medical-yes"
            >
              I have some — let me share
            </button>
          </div>

          {profile.has_medical_considerations === true && (
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }} data-testid="profile-medical-details">
              <div>
                <div className="eyebrow" style={{ marginBottom: 6, fontSize: 10 }}>SKIN CONDITIONS</div>
                <MultiChipRow options={SKIN_CONDITION_OPTIONS} value={profile.skin_conditions} onToggle={(v) => toggleMedList("skin_conditions", v)} dt="profile-skin-cond" />
              </div>
              <div>
                <div className="eyebrow" style={{ marginBottom: 6, fontSize: 10 }}>SCALP CONDITIONS</div>
                <MultiChipRow options={SCALP_CONDITION_OPTIONS} value={profile.scalp_conditions} onToggle={(v) => toggleMedList("scalp_conditions", v)} dt="profile-scalp-cond" />
              </div>
              <div>
                <div className="eyebrow" style={{ marginBottom: 6, fontSize: 10 }}>ALLERGIES</div>
                <MultiChipRow options={ALLERGY_OPTIONS} value={profile.allergies} onToggle={(v) => toggleMedList("allergies", v)} dt="profile-allergy" />
              </div>
              <div>
                <div className="eyebrow" style={{ marginBottom: 6, fontSize: 10 }}>PRESCRIPTION TREATMENTS</div>
                <MultiChipRow options={RX_TREATMENT_OPTIONS} value={profile.prescription_treatments} onToggle={(v) => toggleMedList("prescription_treatments", v)} dt="profile-rx" />
              </div>
              {(profile.gender || "").toLowerCase().includes("fem") && (
                <div style={{ display: "flex", gap: 6 }}>
                  <button
                    type="button"
                    className={`chip ${profile.pregnancy_breastfeeding === true ? "active" : ""}`}
                    onClick={() => update({ pregnancy_breastfeeding: profile.pregnancy_breastfeeding === true ? null : true })}
                    data-testid="profile-pregnancy-yes"
                  >
                    Pregnant / breastfeeding
                  </button>
                </div>
              )}
              <textarea
                className="input"
                placeholder="Anything else? (optional, free-text)"
                rows={2}
                value={profile.other_medical_notes}
                onChange={(e) => update({ other_medical_notes: e.target.value.slice(0, 320) })}
                style={{ resize: "none", fontFamily: "inherit", lineHeight: 1.45 }}
                data-testid="profile-medical-notes"
              />
              <div className="tiny" style={{ color: "var(--text-faint)", padding: "10px 12px", background: "rgba(232,211,185,0.06)", borderLeft: "2px solid var(--gold)", lineHeight: 1.5 }}>
                We&apos;ll still recommend products and routines, but we&apos;ll add a friendly reminder to check with a healthcare professional, avoid your listed allergens, and recommend a 48-hour patch test.
              </div>
            </div>
          )}
        </Field>
      </div>

      {/* ─────────────  FITNESS  ───────────── */}
      <div className="outfit-section" data-testid="profile-fitness-section">
        <div className="eyebrow" style={{ marginBottom: 14 }}>FITNESS</div>

        <Field label="Goal weight (optional)" testId="profile-field-goal-weight" hint="Where you'd like to be — used to pace your calorie target.">
          <input
            className="input" placeholder="e.g. 170 lbs or 77 kg"
            value={profile.goal_weight}
            onChange={(e) => update({ goal_weight: e.target.value })}
            data-testid="profile-goal-weight-input"
          />
        </Field>

        <Field label="Workout experience" testId="profile-field-workout-exp">
          <ChipRow options={WORKOUT_EXP_OPTIONS} value={profile.workout_experience} onPick={(v) => update({ workout_experience: profile.workout_experience === v ? "" : v })} dt="profile-workout-exp" />
        </Field>

        <Field label="Workout location" testId="profile-field-workout-loc">
          <ChipRow options={WORKOUT_LOCATION_OPTIONS} value={profile.workout_location} onPick={(v) => update({ workout_location: profile.workout_location === v ? "" : v })} dt="profile-workout-loc" />
        </Field>
      </div>

      {/* ─────────────  NUTRITION  ───────────── */}
      <div className="outfit-section" data-testid="profile-nutrition-section">
        <div className="eyebrow" style={{ marginBottom: 14 }}>NUTRITION</div>

        <Field label="Dietary preference" testId="profile-field-dietary-pref" hint="The Nutrition Scanner respects this when scoring meals.">
          <ChipRow options={DIETARY_PREF_OPTIONS} value={profile.dietary_preference} onPick={(v) => update({ dietary_preference: profile.dietary_preference === v ? "" : v })} dt="profile-diet-pref" />
        </Field>

        <Field label="Food allergies (optional)" testId="profile-field-food-allergies" hint="Comma-separated. We'll flag any meal that contains them.">
          <input
            className="input" placeholder="e.g. peanuts, shellfish, sesame"
            value={profile.food_allergies.join(", ")}
            onChange={(e) => update({ food_allergies: e.target.value.split(",").map(s => s.trim()).filter(Boolean) })}
            data-testid="profile-food-allergies-input"
          />
        </Field>

        <Field label="Medical dietary restrictions (optional)" testId="profile-field-medical-diet" hint="e.g. low-FODMAP, low-sodium, diabetic, IBS-friendly.">
          <input
            className="input" placeholder="e.g. low-sodium, low-FODMAP"
            value={profile.medical_dietary_restrictions.join(", ")}
            onChange={(e) => update({ medical_dietary_restrictions: e.target.value.split(",").map(s => s.trim()).filter(Boolean) })}
            data-testid="profile-medical-diet-input"
          />
        </Field>

        <Field label="Preferred units" testId="profile-field-units">
          <ChipRow options={UNITS_OPTIONS} value={profile.preferred_units} onPick={(v) => update({ preferred_units: profile.preferred_units === v ? "" : v })} dt="profile-units" />
        </Field>

        <Field label="Daily water goal (ml)" testId="profile-field-water-goal" hint="Leave blank to let the AI use 35 ml per kg of bodyweight.">
          <input
            className="input" inputMode="numeric" placeholder="e.g. 2500"
            value={profile.daily_water_goal_ml}
            onChange={(e) => update({ daily_water_goal_ml: e.target.value.replace(/[^0-9]/g, "").slice(0, 5) })}
            data-testid="profile-water-goal-input"
          />
        </Field>
      </div>

      {/* ─────────────  SKIN  ───────────── */}
      <div className="outfit-section" data-testid="profile-skin-section">
        <div className="eyebrow" style={{ marginBottom: 14 }}>SKIN</div>

        <Field label="Sensitive skin?" testId="profile-field-sensitive-skin">
          <div style={{ display: "flex", gap: 6 }}>
            <button
              type="button"
              className={`chip ${profile.sensitive_skin === false ? "active" : ""}`}
              onClick={() => update({ sensitive_skin: profile.sensitive_skin === false ? null : false })}
              data-testid="profile-sensitive-skin-no"
            >No</button>
            <button
              type="button"
              className={`chip ${profile.sensitive_skin === true ? "active" : ""}`}
              onClick={() => update({ sensitive_skin: profile.sensitive_skin === true ? null : true })}
              data-testid="profile-sensitive-skin-yes"
            >Yes — sensitive</button>
          </div>
        </Field>

        <Field label="Prescription skincare (optional)" testId="profile-field-rx-skincare" hint="Comma-separated. e.g. tretinoin, hydroquinone.">
          <input
            className="input" placeholder="e.g. tretinoin, azelaic acid"
            value={profile.prescription_skincare.join(", ")}
            onChange={(e) => update({ prescription_skincare: e.target.value.split(",").map(s => s.trim()).filter(Boolean) })}
            data-testid="profile-rx-skincare-input"
          />
        </Field>

        <Field label="Skincare ingredient allergies (optional)" testId="profile-field-ingredient-allergies" hint="Comma-separated. Used by Skin Coach to avoid recommendations.">
          <input
            className="input" placeholder="e.g. fragrance, sulfates, lanolin"
            value={profile.ingredient_allergies.join(", ")}
            onChange={(e) => update({ ingredient_allergies: e.target.value.split(",").map(s => s.trim()).filter(Boolean) })}
            data-testid="profile-ingredient-allergies-input"
          />
        </Field>
      </div>

      {error && <div className="error-text" data-testid="profile-error" style={{ marginBottom: 12 }}>{error}</div>}

      <div style={{ display: "flex", gap: 10, marginBottom: 24 }}>
        <button className="btn btn-primary" onClick={save} disabled={saving} data-testid="profile-save-btn" style={{ flex: 1 }}>
          {saving ? (<><div className="spinner small" /> Saving…</>) : (<><Check size={14} /> Save profile</>)}
        </button>
        <button className="btn btn-ghost" onClick={wipe} data-testid="profile-clear-btn" title="Clear profile">
          <Trash2 size={14} />
        </button>
      </div>
    </div>
  );
}
